import { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";
import {
  Process,
  ProcessFormValues,
  useDeleteRecipeProcess,
  useUpdateRecipeProcess
} from "../../../api/recipeSimulator";
import { useGetCountries } from "../../../api/static-data";
import { RecipeTypeClassification } from "../../../config/enums/recipeSimulator.enum";
import { errorView } from "../../../helpers/ErrorHandler";
import {
  buildRecipeProcessPayload,
  normalizeProcessForForm
} from "../../../helpers/ProcessPayloadHelper";
import {
  getExistingProcessesInContext,
  isProcessNameUnique
} from "../../../helpers/ProcessValidationHelper";

import { isEmpty } from "lodash";
import { FormInput } from "../../../components/ui/Input/FormInput/FormInput";
import { FormSelect } from "../../../components/ui/Input/FormSelector/FormSelect";
import { FormTextarea } from "../../../components/ui/Input/FormTextArea/FormTextarea";
import { Card, CardContent } from "../../../components/ui/common/card";
import { Form } from "../../../components/ui/common/form";
import { ScrollArea } from "../../../components/ui/common/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/common/sheet";
import MainBottomMultipleButtonView from "../../../components/ui/layout/BottomButtonView/BottomButtonView";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import { ConfirmModal } from "../../../components/ui/widget/ConfirmModal";
import { DENMARK_COUNTRY_CODE, recipeDataTypeOptions } from "../../../config/constants";
import {
  RecipeDataTypesOption,
  TemperatureUnitOptions
} from "../../../config/enums/IngredientType.enum";
import { RecipeType } from "../../../config/enums/recipeSimulator.enum";
import "./RecipeAddProcessingStepModal.scss";
import SemiSpecific from "./component/SemiSpecific";
import Specific from "./component/Specific";

interface RecipeAddProcessingStepModalProps {
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  recipeDataRefetch?: () => void;
  variantId?: string | null;
  setVariantId?: (id: string | null) => void;
  genericProcess?: any;
  recipeData?: any;
  isOriginal?: boolean;
  process: Process;
  readOnly?: boolean;
}

const RecipeViewProcessingStepModal = ({
  visibleModal,
  setVisibleModal,
  recipeDataRefetch,
  variantId,
  genericProcess,
  recipeData,
  isOriginal,
  process,
  readOnly = false
}: RecipeAddProcessingStepModalProps) => {
  const [enableEditing, setEnableEditing] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  // Router params
  const { recipeId } = useParams();
  const defaultTemperatureUnit = TemperatureUnitOptions.CELSIUS;

  const values = useMemo((): ProcessFormValues => {
    setEnableEditing(false);
    return normalizeProcessForForm(process, {
      defaultCountry: DENMARK_COUNTRY_CODE,
      defaultTemperatureUnit
    });
  }, [process]);

  // API queries
  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const { mutate: updateProcess, isLoading } = useUpdateRecipeProcess();

  // Memoized values
  const countries: any = useMemo(() => {
    if (isCountriesDataSuccess) {
      const getCountryData = countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id,
        alpha_code: c.alpha_code
      }));
      return getCountryData;
    }
  }, [countriesData, isCountriesDataSuccess]);

  const form = useForm<ProcessFormValues>({
    mode: "all",
    defaultValues: values,
    values
  });

  const {
    reset,
    control,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    formState: { errors }
  } = form;

  const currentDataType = useWatch({
    control,
    name: `dataType`
  });

  const currentProcessName = useWatch({
    control,
    name: `processName`
  });

  const dataTypeIsGeneric = currentDataType === RecipeDataTypesOption.GENERIC;
  const dataTypeIsSpecific = currentDataType === RecipeDataTypesOption.SPECIFIC;
  const dataTypeIsSemiSpecific = currentDataType === RecipeDataTypesOption.SEMI_SPECIFIC;

  const { mutate: deleteProcess, isLoading: isProcessDeleting } = useDeleteRecipeProcess();

  // Loading state
  const isLoaderVisible = isCountriesDataLoading || isLoading || isProcessDeleting;

  const onSubmitSingle = () => {
    trigger().then(isValid => {
      if (isValid) {
        const processData = getValues();
        const isSemiSpecific = processData.dataType === RecipeDataTypesOption.SEMI_SPECIFIC;

        // Validate process name uniqueness
        if (recipeData) {
          const existingProcesses = getExistingProcessesInContext(
            recipeData,
            isOriginal ?? false,
            variantId
          );

          // Pass process._id to exclude current process from duplicate check
          if (!isProcessNameUnique(processData.processName, existingProcesses, process?._id)) {
            errorView("Process already exists");
            return;
          }
        }

        const payload = buildRecipeProcessPayload(processData, {
          defaultTemperatureUnit,
          preserveRelationshipFrom: process
        });

        updateProcess(
          {
            recipeId: recipeId ?? "",
            processId: process?._id ?? "",
            variantId: variantId ?? "",
            recipeType: isOriginal ? RecipeType.ORIGINAL : RecipeType.VARIANT,
            process: payload
          },
          {
            onSuccess() {
              setEnableEditing(false);
              recipeDataRefetch && recipeDataRefetch();
            }
          }
        );
      }
    });
  };

  const handleConfirmRemove = () => {
    deleteProcess(
      {
        recipeId: recipeId ?? "",
        processId: process?._id ?? "",
        variantId: variantId ?? "",
        recipeType: isOriginal ? RecipeType.ORIGINAL : RecipeType.VARIANT
      },
      {
        onSuccess: () => {
          setVisibleModal(false);
          setIsRemoveModalOpen(false);
          recipeDataRefetch && recipeDataRefetch();
        },
        onError: () => setIsRemoveModalOpen(false)
      }
    );
  };

  const cancelAndDelete = () => {
    if (enableEditing) {
      reset(values);
      setEnableEditing(false);
      recipeDataRefetch && recipeDataRefetch();
      return;
    }
    setIsRemoveModalOpen(true);
  };

  const updateAndEdit = () => {
    if (enableEditing) {
      handleSubmit(onSubmitSingle)();
      return;
    }
    setEnableEditing(true);
  };

  const handleChange = (value: string) => {
    if (value === RecipeDataTypesOption.GENERIC) {
      form.setValue(`genericProcessId`, "");
    }
  };

  const isProcessNameExiting = useMemo(() => {
    return !isEmpty(currentProcessName);
  }, [currentProcessName]);

  const enableCountry = useMemo(() => {
    return dataTypeIsSpecific || dataTypeIsSemiSpecific;
  }, [dataTypeIsSpecific, dataTypeIsSemiSpecific]);

  // Helper function to check if process is duplicated
  const isProcessDuplicated = useMemo(() => {
    return !!(process?.originalRecipeId && process?.originalProcessId);
  }, [process?.originalRecipeId, process?.originalProcessId]);

  // Helper function to get user-friendly label for recipe type
  const getRecipeTypeLabel = (type?: RecipeTypeClassification): string => {
    switch (type) {
      case RecipeTypeClassification.PUBLIC:
        return "Public";
      case RecipeTypeClassification.DEMOKIT:
        return "Demokit";
      case RecipeTypeClassification.PRIVATE:
        return "Private";
      default:
        return "Private";
    }
  };

  if (isLoaderVisible) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <Sheet open={visibleModal} onOpenChange={setVisibleModal}>
      <SheetContent
        side="right"
        className="tw-w-full sm:tw-max-w-6xl !tw-p-0 tw-flex tw-flex-col tw-gap-0"
      >
        <SheetHeader className="tw-px-6 tw-pt-1 tw-pb-1 tw-border-b tw-border-border tw-shrink-0">
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-py-4">
            <div className="tw-flex-1">
              <SheetTitle className="tw-font-display tw-text-xl">{`${
                enableEditing ? "Edit" : "View"
              } Processing Details`}</SheetTitle>
            </div>
          </div>
        </SheetHeader>

        <Form {...form}>
          <CommonSplitContainer
            topChild={
              <Card className="tw-border-border tw-shadow-none tw-p-0 tw-gap-0 tw-w-full tw-h-full">
                <ScrollArea className="tw-flex-1 tw-px-4 tw-py-4 tw-bg-white">
                  <div className="tw-space-y-4 tw-pb-4 tw-h-full">
                    <CardContent className="tw-px-3.5 tw-py-4 tw-space-y-5 tw-flex tw-flex-col">
                      <div className="tw-text-sm tw-text-muted-foreground">
                        <form
                          onSubmit={e => {
                            e.preventDefault();
                          }}
                        >
                          <div className="tw-space-y-4">
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                              <div className="tw-col-span-full md:tw-col-span-1">
                                <FormInput
                                  control={control}
                                  name={`processName`}
                                  label="Processing Name"
                                  placeholder="Enter Process Name"
                                  required
                                  errorMessage="Please enter process Name"
                                  disabled={!enableEditing}
                                />
                              </div>
                              <div className="tw-col-span-full md:tw-col-span-1">
                                <FormSelect
                                  control={control}
                                  name={`dataType`}
                                  label="Data Type"
                                  placeholder="Select a data type"
                                  required
                                  errorMessage="Please select a data type"
                                  options={recipeDataTypeOptions}
                                  disabled={!enableEditing || !isProcessNameExiting}
                                  onChange={handleChange}
                                />
                              </div>
                              {enableCountry && (
                                <div className="tw-col-span-full md:tw-col-span-1">
                                  <FormSelect
                                    control={control}
                                    name={"country"}
                                    label="Processing Country"
                                    placeholder="Select a processing country"
                                    required={enableCountry}
                                    errorMessage="Please select a processing country"
                                    options={countries}
                                    disabled={!enableEditing || !isProcessNameExiting}
                                  />
                                </div>
                              )}
                            </div>

                            {isProcessNameExiting ? (
                              <>
                                {dataTypeIsGeneric && (
                                  <FormSelect
                                    control={control}
                                    name={`genericProcessId`}
                                    label="Generic Process Type"
                                    placeholder="Select a generic process type"
                                    required={dataTypeIsGeneric}
                                    errorMessage="Please select a generic process type"
                                    options={genericProcess}
                                    disabled={!enableEditing}
                                  />
                                )}

                                {dataTypeIsSpecific && (
                                  <Specific
                                    control={control}
                                    existingProcess={!enableEditing}
                                    dataTypeIsSpecific={dataTypeIsSpecific}
                                    countries={countries}
                                  />
                                )}

                                {dataTypeIsSemiSpecific && (
                                  <SemiSpecific
                                    control={control}
                                    setValue={setValue}
                                    existingProcess={!enableEditing}
                                  />
                                )}
                                <FormTextarea
                                  control={control}
                                  name={`processNote`}
                                  label="Notes"
                                  placeholder="Add notes about this process"
                                  errorMessage="Please enter processing Note"
                                  disabled={!enableEditing}
                                />
                              </>
                            ) : null}
                          </div>
                        </form>
                      </div>
                    </CardContent>
                  </div>
                </ScrollArea>
              </Card>
            }
            bottomChild={
              <MainBottomMultipleButtonView
                leftButtons={
                  readOnly
                    ? [
                        {
                          title: "Close",
                          onClick: () => setVisibleModal(false),
                          type: "button",
                          buttonOutLine: true,
                          buttonView: true
                        }
                      ]
                    : [
                        {
                          title: "Cancel",
                          onClick: () => setVisibleModal(false),
                          type: "button",
                          buttonOutLine: true,
                          buttonView: !enableEditing
                        },
                        {
                          title: enableEditing ? "Cancel" : "Delete",
                          onClick: () => cancelAndDelete(),
                          type: "button",
                          buttonOutLine: true,
                          buttonView: true,
                          theme: "red"
                        }
                      ]
                }
                rightButtons={
                  readOnly
                    ? []
                    : [
                        {
                          title: enableEditing ? "Update" : "Edit",
                          onClick: () => updateAndEdit(),
                          type: "submit",
                          buttonView: true
                        }
                      ]
                }
              />
            }
          />
        </Form>
        <ConfirmModal
          isOpen={isRemoveModalOpen}
          onClose={() => setIsRemoveModalOpen(false)}
          onConfirm={handleConfirmRemove}
          title="Remove Process"
          message={`Are you sure you want to remove "${currentProcessName}" ?`}
          confirmText="Remove"
          cancelText="Cancel"
          confirmVariant="destructive"
        />
      </SheetContent>
    </Sheet>
  );
};

export default RecipeViewProcessingStepModal;
